// src/pages/ContactPage.js
import React from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";

export default function ContactPage() {
  const navigate = useNavigate();

  const navButtonStyle = {
    background: "transparent",
    color: "rgba(255,255,255,0.85)",
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    fontWeight: "500",
    textDecoration: "none",
    transition: "all 0.2s ease",
  };

  const handleHover = (e, entering) => {
    e.target.style.color = entering ? "#ffffff" : "rgba(255,255,255,0.85)";
    e.target.style.transform = entering ? "translateY(-1px)" : "translateY(0)";
  };

  return (
    <>
      <Helmet>
  <title>Contact PBIS Portal</title>
  <meta
    name="description"
    content="Contact PBIS Portal to learn how your school can implement PBIS and improve student behavior systems."
  />
  <link rel="canonical" href="https://pbisportal.com/contact" />
</Helmet>
    
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" }}>
      
      {/* HEADER */}
      <header style={headerStyle}>
        <Link to="/" style={logoStyle}>
          🎯 PBIS Portal
        </Link>

        <div style={navContainer}>
          <NavButton label="About" to="/about" {...{ navigate, navButtonStyle, handleHover }} />
          <NavButton label="Features" to="/features" {...{ navigate, navButtonStyle, handleHover }} />
          <NavButton label="What is PBIS" to="/what-is-pbis" {...{ navigate, navButtonStyle, handleHover }} />
          <NavButton label="Contact" to="/contact" {...{ navigate, navButtonStyle, handleHover }} />

          <div style={divider} />

          <Link to="/login" style={loginButton}>
            Log In
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section style={heroStyle}>
        <h1 style={heroTitle}>Let’s Connect 👋</h1>
        <p style={heroText}>
          Whether you're exploring PBIS for your school, looking for a better way to track student behavior,
          or just have questions — I’d love to hear from you.
        </p>
      </section>

      {/* CONTENT */}
      <section style={contentContainer}>
        
        <div style={cardStyle}>
          <h2 style={sectionTitle}>📬 Reach Out Anytime</h2>

          <p style={paragraph}>
            If you're interested in PBIS Portal,
            need help thinking through PBIS implementation, or just want to see if this
            would be a good fit for your school — feel free to reach out.
          </p>

          <div style={emailBox}>
            <span style={{ fontSize: "1.1rem", fontWeight: "600" }}>
              ryan@pbisportal.com
            </span>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitle}>💡 What You Can Reach Out About</h2>

          <ul style={listStyle}>
            <li>Setting up PBIS at your school</li>
            <li>Questions about PBIS Portal features</li>
            <li>Pricing or school onboarding</li>
            <li>Feedback or feature requests</li>
            <li>General PBIS questions</li>
          </ul>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitle}>🤝 No Pressure — Just a Conversation</h2>

          <p style={paragraph}>
            There’s no sales pressure here. My goal is to help schools build
            positive systems that actually work. If PBIS Portal is a good fit,
            great — and if not, I’m still happy to help however I can.
          </p>
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
          <button
            onClick={() => window.location.href = "mailto:ryan@pbisportal.com"}
            style={ctaButton}
          >
            Send an Email
          </button>
        </div>

      </section>
      </div>
</>
);
}

// 🔹 Reusable Nav Button
const NavButton = ({ label, to, navButtonStyle, handleHover }) => (
  <Link
    to={to}
    style={navButtonStyle}
    onMouseEnter={(e) => handleHover(e, true)}
    onMouseLeave={(e) => handleHover(e, false)}
  >
    {label}
  </Link>
);

// 🎨 Styles
const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "1rem 2rem",
  backgroundColor: "#1e3a8a",
  color: "white",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
};

const logoStyle = {
  fontSize: "1.8rem",
  fontWeight: "700",
  cursor: "pointer",
  color: "inherit",
  textDecoration: "none",
};

const navContainer = {
  display: "flex",
  alignItems: "center",
  gap: "1.5rem",
};

const divider = {
  width: "1px",
  height: "20px",
  background: "rgba(255,255,255,0.3)",
};

const loginButton = {
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "0.65rem 1.2rem",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600",
};

const heroStyle = {
  textAlign: "center",
  padding: "4rem 2rem",
  background: "linear-gradient(135deg, #e0f2fe 0%, #ecfeff 100%)",
};

const heroTitle = {
  fontSize: "2.8rem",
  fontWeight: "800",
  marginBottom: "1rem",
};

const heroText = {
  fontSize: "1.2rem",
  maxWidth: "700px",
  margin: "0 auto",
  color: "#475569",
  lineHeight: "1.7",
};

const contentContainer = {
  maxWidth: "900px",
  margin: "3rem auto",
  padding: "0 2rem",
};

const cardStyle = {
  backgroundColor: "white",
  padding: "1.75rem",
  borderRadius: "14px",
  marginBottom: "1.5rem",
  boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
};

const sectionTitle = {
  fontSize: "1.4rem",
  fontWeight: "700",
  marginBottom: "0.75rem",
};

const paragraph = {
  fontSize: "1.05rem",
  color: "#475569",
  lineHeight: "1.7",
};

const listStyle = {
  paddingLeft: "1.25rem",
  color: "#475569",
  lineHeight: "1.8",
};

const emailBox = {
  marginTop: "1rem",
  padding: "0.75rem 1rem",
  backgroundColor: "#eff6ff",
  borderRadius: "8px",
  display: "inline-block",
};

const ctaButton = {
  backgroundColor: "#6366f1",
  color: "white",
  fontSize: "1rem",
  padding: "0.75rem 1.75rem",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600",
  boxShadow: "0 6px 18px rgba(99, 102, 241, 0.3)",
};
